import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { TreeStructure, Sparkle, FloppyDisk, ArrowsClockwise, CalendarBlank, Warning, Gear, X, Plus } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DateField } from '@/components/ui/date-time-field';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useT } from '@/i18n/LanguageContext';
import { mfgApi } from './mfgApi';
import { useDepartment } from './DepartmentContext';
import ItemPickerField from './ItemPickerField';
import EquipmentPickerDialog, { EQUIPMENT_STATUS_STYLE } from './EquipmentPickerDialog';

const NONE = '__none__';
const EMPTY = { item_id: '', qty: 1, due_date: '', equipment_id: '', location_id: '', work_center_id: '', scheduled_start: '', scheduled_end: '', manufacturing_no: '', notes: '' };

export default function WorkOrderFormDialog({ open, workOrder, onClose, onSaved }) {
  const { t } = useT();
  const { active } = useDepartment();
  const isAuto = active?.code === 'SNS';
  const editing = !!workOrder;
  const [form, setForm] = useState(EMPTY);
  const [items, setItems] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [locations, setLocations] = useState([]);
  const [workCenters, setWorkCenters] = useState([]);
  const [explosion, setExplosion] = useState(null);
  const [bomEquipment, setBomEquipment] = useState([]);   // 部品表に登録された使用設備（状態チェック用）
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [seqInput, setSeqInput] = useState('');
  const [showSeq, setShowSeq] = useState(false);
  const [savingSeq, setSavingSeq] = useState(false);
  const [eqPickerOpen, setEqPickerOpen] = useState(false);

  const loadAutoNo = useCallback(() => {
    mfgApi.mfgNoPreview().then((r) => setPreview(r.preview)).catch(() => {});
    mfgApi.mfgNoSeq().then((r) => setSeqInput(String(r.next_serial))).catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return;
    mfgApi.items().then(setItems).catch(() => {});
    mfgApi.equipment().then(setEquipment).catch(() => {});
    mfgApi.locations().then(setLocations).catch(() => {});
    mfgApi.workCenters().then(setWorkCenters).catch(() => {});
    setForm(workOrder
      ? { ...EMPTY, ...workOrder, due_date: workOrder.due_date || '', equipment_id: workOrder.equipment_id || '', location_id: workOrder.location_id || '', work_center_id: workOrder.work_center_id || '', scheduled_start: workOrder.scheduled_start || '', scheduled_end: workOrder.scheduled_end || '', manufacturing_no: workOrder.manufacturing_no || '' }
      : EMPTY);
    setExplosion(null);
    setShowSeq(false);
    if (isAuto && !workOrder) loadAutoNo();
  }, [open, workOrder, isAuto, loadAutoNo]);

  const saveSeq = async () => {
    const n = Number(seqInput);
    if (!(n >= 1)) { toast.error('開始番号は1以上で指定してください'); return; }
    setSavingSeq(true);
    try { await mfgApi.setMfgNoSeq({ next_serial: n }); loadAutoNo(); setShowSeq(false); toast.success(t('mfg.common.saved')); }
    catch (e) { toast.error(e?.response?.data?.detail || t('mfg.common.error')); }
    finally { setSavingSeq(false); }
  };

  const loadPreview = useCallback((id, qty) => {
    if (!id || !(Number(qty) > 0)) { setExplosion(null); return; }
    mfgApi.explodeBom(id, Number(qty)).then(setExplosion).catch(() => setExplosion(null));
  }, []);

  useEffect(() => {
    if (!open) return;
    const tm = setTimeout(() => loadPreview(form.item_id, form.qty), 250);
    return () => clearTimeout(tm);
  }, [open, form.item_id, form.qty, loadPreview]);

  // 品目の部品表から使用設備を取得（メンテ中・故障の警告用）
  useEffect(() => {
    if (!open || !form.item_id) { setBomEquipment([]); return; }
    mfgApi.bom(form.item_id).then((d) => setBomEquipment(d.equipment || [])).catch(() => setBomEquipment([]));
  }, [open, form.item_id]);

  const BAD_STATUS = { maintenance: 'メンテ中', breakdown: '故障' };
  const statusLabelOf = (e) => equipment.find((x) => x.id === e.id)?.status_label || e.status_label || BAD_STATUS[e.status] || e.status;
  const badMachines = (() => {
    const list = bomEquipment.filter((e) => BAD_STATUS[e.status]);
    const sel = equipment.find((x) => x.id === form.equipment_id);
    if (sel && BAD_STATUS[sel.status] && !list.some((e) => e.id === sel.id)) list.push(sel);
    return list;
  })();
  const hasBreakdown = badMachines.some((e) => e.status === 'breakdown');

  const save = async () => {
    if (!form.item_id) { toast.error(t('mfg.wo.selectProduct')); return; }
    if (!(Number(form.qty) > 0)) { toast.error(t('mfg.wo.qty')); return; }
    setBusy(true);
    try {
      const body = {
        item_id: form.item_id, qty: Number(form.qty),
        due_date: form.due_date || null,
        equipment_id: form.equipment_id || null,
        location_id: form.location_id || null,
        work_center_id: form.work_center_id || null,
        scheduled_start: form.scheduled_start || null,
        scheduled_end: form.scheduled_end || null,
        manufacturing_no: form.manufacturing_no || null,
        notes: form.notes || null,
      };
      const res = workOrder ? await mfgApi.updateWorkOrder(workOrder.id, body) : await mfgApi.createWorkOrder(body);
      toast.success(t('mfg.common.saved')); onSaved?.(res);
    } catch (e) { toast.error(e?.response?.data?.detail || t('mfg.common.error')); }
    finally { setBusy(false); }
  };

  const directComps = (explosion?.tree?.children || []).map((c) => ({
    ...c, shortage: Math.max(0, (c.required || 0) - (c.on_hand || 0)),
  }));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="wo-form-dialog">
        <DialogHeader><DialogTitle>{workOrder ? t('mfg.wo.edit') : t('mfg.wo.new')}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.wo.product')}</Label>
            <div className="mt-1">
              <ItemPickerField testid="wo-product" value={form.item_id || ''} initialItems={items}
                onChange={(id) => setForm((p) => ({ ...p, item_id: id }))} placeholderKey="mfg.wo.selectProduct" />
            </div></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.wo.qty')}</Label>
              <Input type="number" data-testid="wo-qty-input" value={form.qty} onChange={(e) => setForm((p) => ({ ...p, qty: e.target.value }))} className="h-10 mt-1 text-right" /></div>
            <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.wo.dueDate')}</Label>
              <DateField testId="wo-due-input" value={form.due_date || ''} onChange={(v) => setForm((p) => ({ ...p, due_date: v }))} className="mt-1" inputClassName="h-10" warnNonWorking /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-zinc-600 flex items-center gap-1.5"><Gear size={13} weight="duotone" className="text-sky-500" /> {t('mfg.wo.equipment')}</Label>
              {(() => {
                const sel = equipment.find((x) => x.id === form.equipment_id);
                const bomIds = new Set(bomEquipment.map((e) => e.id));
                const suggestions = bomEquipment.filter((e) => e.id !== form.equipment_id).slice(0, 4);
                const hasAny = equipment.length > 0 || bomEquipment.length > 0;
                if (!hasAny) {
                  return <p className="mt-1.5 text-[11px] text-zinc-400" data-testid="wo-equipment-none-registered">{t('mfg.wo.equipmentNoneRegistered')}</p>;
                }
                return (
                  <div className="mt-1 space-y-1.5">
                    {sel ? (
                      <div data-testid={`wo-equipment-card-${sel.id}`}
                        className="flex items-center gap-2 rounded-lg border border-sky-200 bg-white px-2.5 py-2">
                        <Gear size={16} weight="duotone" className="text-sky-500 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-xs font-medium text-zinc-800">{sel.name}</p>
                          <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                            {sel.asset_no && <span className="font-mono text-[10px] text-zinc-400">{sel.asset_no}</span>}
                            {(sel.maker || sel.model) && <span className="text-[10px] text-zinc-400">{[sel.maker, sel.model].filter(Boolean).join(' ')}</span>}
                            {(sel.status_label || sel.status) && (
                              <span className={`rounded px-1 py-px text-[9px] font-medium ${EQUIPMENT_STATUS_STYLE[sel.status] || 'bg-zinc-100 text-zinc-500'}`}>
                                {sel.status_label || sel.status}
                              </span>
                            )}
                            {bomIds.has(sel.id) && (
                              <span className="rounded bg-sky-50 px-1 py-px text-[9px] font-medium text-sky-700 border border-sky-200">{t('mfg.wo.equipmentFromBom')}</span>
                            )}
                          </div>
                        </div>
                        <Button size="sm" variant="ghost" data-testid="wo-equipment-change" onClick={() => setEqPickerOpen(true)} className="h-7 text-sky-600 shrink-0">
                          {t('mfg.wo.equipmentChange')}
                        </Button>
                        <button type="button" data-testid="wo-equipment-clear" onClick={() => setForm((p) => ({ ...p, equipment_id: '' }))}
                          className="shrink-0 text-zinc-400 hover:text-rose-600" title={t('mfg.wo.equipmentClear')}><X size={14} /></button>
                      </div>
                    ) : (
                      <Button type="button" size="sm" variant="outline" data-testid="wo-equipment-pick"
                        onClick={() => setEqPickerOpen(true)}
                        className="h-9 w-full justify-start border-dashed border-sky-200 bg-sky-50/40 text-sky-700 hover:bg-sky-50 hover:text-sky-800">
                        <Plus size={14} className="mr-1.5" /> {t('mfg.wo.equipmentPick')}
                      </Button>
                    )}
                    {!sel && suggestions.length > 0 && (
                      <div className="rounded-lg border border-sky-100 bg-sky-50/40 px-2 py-1.5" data-testid="wo-equipment-suggestions">
                        <div className="flex items-center gap-1 text-[10px] font-semibold text-sky-700 mb-1">
                          <Sparkle size={11} weight="fill" /> {t('mfg.wo.equipmentSuggested')}
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {suggestions.map((e) => (
                            <button key={e.id} type="button" data-testid={`wo-equipment-suggest-${e.id}`}
                              onClick={() => setForm((p) => ({ ...p, equipment_id: e.id }))}
                              className="inline-flex items-center gap-1 rounded border border-sky-200 bg-white px-1.5 py-0.5 text-[11px] text-zinc-700 hover:bg-sky-50">
                              <Gear size={11} weight="duotone" className="text-sky-500" />
                              <span className="max-w-[140px] truncate">{e.name}</span>
                              {e.asset_no && <span className="font-mono text-[9px] text-zinc-400">{e.asset_no}</span>}
                              {(e.status_label || e.status) && (
                                <span className={`rounded px-1 text-[9px] font-medium ${EQUIPMENT_STATUS_STYLE[e.status] || 'bg-zinc-100 text-zinc-500'}`}>
                                  {e.status_label || e.status}
                                </span>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <p className="text-[11px] text-zinc-400">{t('mfg.wo.equipmentHint')}</p>
                  </div>
                );
              })()}
            </div>
            <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.wo.location')}</Label>
              <Select value={form.location_id || NONE} onValueChange={(v) => setForm((p) => ({ ...p, location_id: v === NONE ? '' : v }))}>
                <SelectTrigger data-testid="wo-location-select" className="h-10 mt-1"><SelectValue placeholder={t('mfg.common.none')} /></SelectTrigger>
                <SelectContent><SelectItem value={NONE}>{t('mfg.common.none')}</SelectItem>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.code}{l.name ? ` — ${l.name}` : ''}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          {/* 使用設備の状態警告 — 部品表の設備 or 選択中の設備がメンテ中/故障 */}
          {badMachines.length > 0 && (
            <div className={`flex items-start gap-2 rounded-lg border px-3 py-2 ${hasBreakdown ? 'border-rose-200 bg-rose-50' : 'border-amber-200 bg-amber-50'}`} data-testid="wo-equipment-warning">
              <Warning size={15} weight="fill" className={`shrink-0 mt-0.5 ${hasBreakdown ? 'text-rose-500' : 'text-amber-500'}`} />
              <div className={`text-xs ${hasBreakdown ? 'text-rose-800' : 'text-amber-800'}`}>
                <p className="font-semibold">{t('mfg.wo.equipWarnTitle')}</p>
                <p className="mt-0.5" data-testid="wo-equipment-warning-list">{badMachines.map((e) => `${e.name}（${statusLabelOf(e)}）`).join('、')}</p>
                <p className={`text-[11px] mt-0.5 ${hasBreakdown ? 'text-rose-600/80' : 'text-amber-600/80'}`}>{t('mfg.wo.equipWarnHint')}</p>
              </div>
            </div>
          )}
          {/* 生産計画 — 作業区 & スケジュール */}
          <div className="rounded-lg border border-zinc-200 bg-zinc-50/60 p-3 space-y-3" data-testid="wo-schedule-box">
            <div className="flex items-center gap-1.5"><CalendarBlank size={15} className="text-orange-500" weight="duotone" /><span className="text-xs font-semibold text-zinc-600">{t('mfg.sched.title')}</span></div>
            <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.sched.workCenter')}</Label>
              <Select value={form.work_center_id || NONE} onValueChange={(v) => setForm((p) => ({ ...p, work_center_id: v === NONE ? '' : v }))}>
                <SelectTrigger data-testid="wo-workcenter-select" className="h-10 mt-1"><SelectValue placeholder={t('mfg.common.none')} /></SelectTrigger>
                <SelectContent><SelectItem value={NONE}>{t('mfg.common.none')}</SelectItem>{workCenters.map((w) => <SelectItem key={w.id} value={w.id}>{w.name || w.code} ({w.lead_time_days}{t('mfg.wc.days')})</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.sched.start')}</Label>
                <DateField testId="wo-sched-start" value={form.scheduled_start || ''} onChange={(v) => setForm((p) => ({ ...p, scheduled_start: v }))} className="mt-1" inputClassName="h-10" /></div>
              <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.sched.end')}</Label>
                <DateField testId="wo-sched-end" value={form.scheduled_end || ''} onChange={(v) => setForm((p) => ({ ...p, scheduled_end: v }))} className="mt-1" inputClassName="h-10" /></div>
            </div>
            <p className="text-[11px] text-zinc-400">{t('mfg.sched.autoHint')}</p>
          </div>
          <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.wo.mfgNo')}</Label>
            {isAuto ? (
              editing ? (
                <div className="mt-1 flex items-center gap-2 rounded-lg border border-orange-200 bg-orange-50 px-3 h-10" data-testid="wo-mfgno-auto">
                  <Sparkle size={15} weight="duotone" className="text-orange-500" />
                  <span className="font-mono font-bold text-orange-700">{form.manufacturing_no || '—'}</span>
                  <span className="text-[11px] text-zinc-400 ml-auto">{t('mfg.mfgno.autoIssue')}</span>
                </div>
              ) : (
                <div className="mt-1 space-y-2">
                  <div className="flex items-center gap-2 rounded-lg border border-orange-200 bg-orange-50 px-3 h-10" data-testid="wo-mfgno-auto">
                    <Sparkle size={15} weight="duotone" className="text-orange-500" />
                    <span className="text-[11px] text-zinc-500">{t('mfg.mfgno.next')}:</span>
                    <span className="font-mono font-bold text-orange-700" data-testid="wo-mfgno-preview">{preview || '—'}</span>
                    <button type="button" data-testid="wo-mfgno-reset-toggle" onClick={() => setShowSeq((s) => !s)} className="ml-auto inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-orange-600">
                      <ArrowsClockwise size={13} /> {t('mfg.mfgno.startNo')}
                    </button>
                  </div>
                  {showSeq && (
                    <div className="flex items-end gap-2 rounded-lg border border-zinc-200 bg-zinc-50 p-2" data-testid="wo-mfgno-seq-box">
                      <div><Label className="text-[11px] text-zinc-500">{t('mfg.mfgno.startNo')}</Label>
                        <Input data-testid="wo-mfgno-seq-input" type="number" min="1" value={seqInput} onChange={(e) => setSeqInput(e.target.value)} className="h-9 w-28 mt-0.5 font-mono" /></div>
                      <Button type="button" variant="outline" data-testid="wo-mfgno-seq-save" onClick={saveSeq} disabled={savingSeq} className="h-9 border-orange-200 text-orange-700 hover:bg-orange-50">
                        <FloppyDisk size={14} className="mr-1" /> {t('mfg.common.save')}
                      </Button>
                    </div>
                  )}
                </div>
              )
            ) : (
              <Input data-testid="wo-mfgno-input" value={form.manufacturing_no || ''} onChange={(e) => setForm((p) => ({ ...p, manufacturing_no: e.target.value }))} placeholder={t('mfg.mfgno.noPlaceholder')} className="h-10 mt-1 font-mono" />
            )}
          </div>
          <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.common.notes')}</Label>
            <Input value={form.notes || ''} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} className="h-10 mt-1" /></div>

          {/* live components preview */}
          <div className="rounded-lg border border-zinc-200 bg-zinc-50/60 p-3" data-testid="wo-preview">
            <div className="flex items-center gap-1.5 mb-2"><TreeStructure size={15} className="text-orange-500" /><span className="text-xs font-semibold text-zinc-600">{t('mfg.wo.preview')}</span></div>
            {!form.item_id || !(Number(form.qty) > 0) ? (
              <p className="text-xs text-zinc-400 py-2">{t('mfg.wo.previewHint')}</p>
            ) : directComps.length === 0 ? (
              <p className="text-xs text-zinc-400 py-2" data-testid="wo-preview-nobom">{t('mfg.wo.noComponents')}</p>
            ) : (
              <table className="w-full text-xs" data-testid="wo-preview-table">
                <thead><tr className="text-zinc-400 border-b border-zinc-200"><th className="text-left py-1">{t('mfg.bom.component')}</th><th className="text-right py-1">{t('mfg.wo.required')}</th><th className="text-right py-1">{t('mfg.wo.onHand')}</th><th className="text-right py-1">{t('mfg.wo.shortage')}</th></tr></thead>
                <tbody>{directComps.map((l) => (
                  <tr key={l.item_id} className="border-b border-zinc-200">
                    <td className="py-1 text-zinc-700">{l.name}</td>
                    <td className="py-1 text-right tabular-nums">{l.required} {l.uom || ''}</td>
                    <td className="py-1 text-right tabular-nums">{l.on_hand}</td>
                    <td className="py-1 text-right tabular-nums">{l.shortage > 0 ? <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">{l.shortage}</Badge> : <span className="text-emerald-600">0</span>}</td>
                  </tr>
                ))}</tbody>
              </table>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('mfg.common.cancel')}</Button>
          <Button data-testid="wo-save-btn" onClick={save} disabled={busy} className="bg-teal-700 hover:bg-teal-800">{busy ? t('mfg.common.saving') : t('mfg.common.save')}</Button>
        </DialogFooter>
      </DialogContent>
      <EquipmentPickerDialog open={eqPickerOpen} onOpenChange={setEqPickerOpen}
        equipment={equipment}
        onPick={(e) => setForm((p) => ({ ...p, equipment_id: e.id }))} />
    </Dialog>
  );
}
